// A profile's Access tab (GB-05): who sees it, and the agent-trigger form's rules. The relay is
// the real gate (mcp/identity-token-rpc.ts, agent-trigger-relay.ts); this only decides what shows.
// The form's bounds mirror cyborg-messages.ts and agent-trigger.ts, pinned by agent-access.test.ts.
import { z } from "zod";
import type {
  McpIdentityToken,
  AgentTrigger,
  AgentTriggerDelivery,
  AgentTriggerFilters,
  AgentTriggerPatch,
} from "$lib/core/client.js";
import type { StatusTone } from "$lib/color/status-tone.js";
import { isMe, type ViewerIdentity } from "$lib/session-scope.js";
import { isWorkspaceAdmin, type WorkspaceRole } from "$lib/workspace-role.js";

export type AccessIdentity =
  | { type: "cybo"; id: string; createdBy: string | null | undefined }
  | { type: "user"; id: string };

/** The agent's owner (a member) or a workspace admin; for a person, themself or an admin. */
export function canSeeAccessTab(
  identity: AccessIdentity,
  role: WorkspaceRole | null | undefined,
  viewer: ViewerIdentity,
): boolean {
  if (isWorkspaceAdmin(role)) return true;
  if (identity.type === "user") return isMe(identity.id, viewer);
  return role === "member" && isMe(identity.createdBy, viewer);
}

/** Why a key can't act right now, or undefined when it can: switched off, expired, identity gone. */
export function tokenInactiveReason(
  t: Pick<McpIdentityToken, "enabled" | "actsAs" | "expiresAt">,
  now: number,
): "off" | "expired" | "identity_gone" | undefined {
  if (!t.enabled) return "off";
  if (typeof t.expiresAt === "number" && t.expiresAt < now) return "expired";
  return t.actsAs?.deleted ? "identity_gone" : undefined;
}

export const AGENT_TRIGGER_RATE_MIN = 1;
export const AGENT_TRIGGER_RATE_MAX = 60;
export const AGENT_TRIGGER_RATE_DEFAULT = 10;
export const AUTH_HEADER_MAX_CHARS = 4096;
export const DEFAULT_TRIGGER_FILTERS: Readonly<AgentTriggerFilters> = Object.freeze({
  mention: true,
  dm: true,
  threadFollowup: false,
  fromAgents: false,
});

const RATE_PROBLEM = `The rate limit must be a whole number from ${AGENT_TRIGGER_RATE_MIN} to ${AGENT_TRIGGER_RATE_MAX} per minute.`;

function isHttpsUrl(raw: string): boolean {
  return URL.canParse(raw) && new URL(raw).protocol === "https:";
}

// oxlint-disable-next-line no-control-regex -- refusing control characters is the point
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

export const AgentTriggerFormSchema = z.object({
  name: z.string().trim().max(100, "Keep the name under 100 characters."),
  url: z.string().trim().refine(isHttpsUrl, "Enter the bot's https:// webhook URL."),
  authHeader: z
    .string()
    .max(AUTH_HEADER_MAX_CHARS, "The Authorization value is too long.")
    .refine((v) => !CONTROL_CHARS.test(v), "The Authorization value has invalid characters."),
  rate: z.coerce
    .number(RATE_PROBLEM)
    .int(RATE_PROBLEM)
    .min(AGENT_TRIGGER_RATE_MIN, RATE_PROBLEM)
    .max(AGENT_TRIGGER_RATE_MAX, RATE_PROBLEM),
  filters: z
    .object({
      mention: z.boolean(),
      dm: z.boolean(),
      threadFollowup: z.boolean(),
      fromAgents: z.boolean(),
    })
    .refine(
      (f) => f.mention || f.dm || f.threadFollowup,
      "Pick at least one: mentions, DMs or thread follow-ups.",
    ),
});

export interface TriggerDraft {
  name: string;
  url: string;
  /** Write-only: "" keeps the saved value on an edit. */
  authHeader: string;
  removeAuthHeader: boolean;
  filters: AgentTriggerFilters;
  rate: string;
}

export type TriggerField = "name" | "url" | "authHeader" | "rate" | "filters";
export type TriggerFormErrors = Partial<Record<TriggerField, string>>;

export function draftFor(trigger?: AgentTrigger): TriggerDraft {
  return {
    name: trigger?.name ?? "",
    url: trigger?.url ?? "",
    authHeader: "",
    removeAuthHeader: false,
    filters: { ...DEFAULT_TRIGGER_FILTERS, ...trigger?.filters },
    rate: String(trigger?.rateLimitPerMinute ?? AGENT_TRIGGER_RATE_DEFAULT),
  };
}

export type TriggerFormResult =
  | { ok: true; value: z.infer<typeof AgentTriggerFormSchema> }
  | { ok: false; errors: TriggerFormErrors };

export function validateTriggerDraft(draft: TriggerDraft): TriggerFormResult {
  const parsed = AgentTriggerFormSchema.safeParse(draft);
  if (parsed.success) return { ok: true, value: parsed.data };
  const errors: TriggerFormErrors = {};
  for (const issue of parsed.error.issues) {
    const field = issue.path[0];
    if (typeof field === "string" && !(field in errors)) {
      errors[field as TriggerField] = issue.message; // cast: path[0] is a key of the schema above
    }
  }
  return { ok: false, errors };
}

type FormValue = z.infer<typeof AgentTriggerFormSchema>;

/** A new trigger's request: every field the form holds. */
export function createInput(value: FormValue): AgentTriggerPatch & { url: string } {
  return {
    url: value.url,
    ...(value.name ? { name: value.name } : {}),
    filters: value.filters,
    rateLimitPerMinute: value.rate,
    ...(value.authHeader ? { authHeader: value.authHeader } : {}),
  };
}

/** Only what changed: the relay re-scopes a trigger whose URL moves, so an untouched URL is not sent. */
export function updatePatch(
  existing: AgentTrigger,
  value: FormValue,
  removeAuthHeader: boolean,
): AgentTriggerPatch {
  const patch: AgentTriggerPatch = {};
  if (value.url !== existing.url) patch.url = value.url;
  if (value.name && value.name !== existing.name) patch.name = value.name;
  const before = { ...DEFAULT_TRIGGER_FILTERS, ...existing.filters };
  const keys = Object.keys(before) as Array<keyof AgentTriggerFilters>; // cast: the four filter keys
  if (keys.some((k) => before[k] !== value.filters[k])) patch.filters = value.filters;
  if (value.rate !== existing.rateLimitPerMinute) patch.rateLimitPerMinute = value.rate;
  if (value.authHeader) patch.authHeader = value.authHeader;
  else if (removeAuthHeader) patch.authHeader = null; // null: the wire's "remove it"
  return patch;
}

export function filtersSummary(filters: AgentTriggerFilters | undefined): string {
  const f = { ...DEFAULT_TRIGGER_FILTERS, ...filters };
  const on = [
    f.mention && "mentions",
    f.dm && "DMs",
    f.threadFollowup && "thread follow-ups",
    f.fromAgents && "other agents",
  ].filter((x): x is string => typeof x === "string");
  return on.length ? `Wakes on ${on.join(", ")}` : "Wakes on nothing";
}

const DELIVERY_TONE: Readonly<Record<string, StatusTone>> = {
  success: "ready",
  failure: "failed",
  rate_limited: "attention",
  dropped: "neutral",
  sending: "progress",
  interrupted: "attention",
};

const DELIVERY_LABEL: Readonly<Record<string, string>> = {
  success: "Delivered",
  failure: "Failed",
  rate_limited: "Rate limited",
  dropped: "Dropped",
  sending: "Sending",
  interrupted: "Interrupted",
};

const REASON_LABEL: Readonly<Record<string, string>> = {
  mention: "Mention",
  dm: "DM",
  thread_followup: "Thread follow-up",
  test: "Test",
};

function messagesLabel(coalesced: number | null | undefined): string {
  const n = coalesced ?? 0;
  if (n > 1) return `${n} messages`;
  return n === 1 ? "1 message" : "";
}

function responseLabel(d: AgentTriggerDelivery): string {
  if (typeof d.responseStatus === "number") return `HTTP ${d.responseStatus}`;
  return d.errorCode ?? "—";
}

export function deliveryView(d: AgentTriggerDelivery): {
  tone: StatusTone;
  label: string;
  reason: string;
  code: string;
  messages: string;
} {
  return {
    tone: DELIVERY_TONE[d.status] ?? "neutral",
    label: DELIVERY_LABEL[d.status] ?? d.status,
    reason: (d.reason && REASON_LABEL[d.reason]) || d.reason || "—",
    code: responseLabel(d),
    messages: messagesLabel(d.coalesced),
  };
}
